import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  Check,
  X,
  AlertTriangle,
  Database,
  Upload,
  Loader2,
  Eye,
  Trash2,
  Edit3,
  Save,
  Sparkles,
  Link2,
  ShieldCheck,
  ExternalLink,
  CalendarX,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const KB_SOURCE_TYPES = [
  { value: "official_gov", label: "Official / Government" },
  { value: "campaign_site", label: "Campaign Site" },
  { value: "news", label: "News Article" },
  { value: "chair_directive", label: "Chair Directive" },
  { value: "bill_text", label: "Bill / Legislation" },
  { value: "other", label: "Other" },
];

const daysAgo = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};

const isValidUrl = (url) => {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const STATUS_CONFIG = {
  uploaded: {
    label: "Uploaded",
    bg: "bg-slate-100",
    text: "text-slate-600",
    icon: Upload,
  },
  parsing: {
    label: "Parsing",
    bg: "bg-blue-100",
    text: "text-blue-600",
    icon: Loader2,
  },
  parsed: {
    label: "Parsed",
    bg: "bg-amber-100",
    text: "text-amber-700",
    icon: Clock,
  },
  approved: {
    label: "Approved",
    bg: "bg-green-100",
    text: "text-green-700",
    icon: CheckCircle,
  },
  embedding: {
    label: "Embedding",
    bg: "bg-blue-100",
    text: "text-blue-600",
    icon: Loader2,
  },
  embedded: {
    label: "Embedded",
    bg: "bg-green-100",
    text: "text-green-800",
    icon: Database,
  },
  denied: {
    label: "Denied",
    bg: "bg-red-100",
    text: "text-red-600",
    icon: XCircle,
  },
  failed: {
    label: "Failed",
    bg: "bg-red-100",
    text: "text-red-700",
    icon: AlertTriangle,
  },
  pending_review: {
    label: "Pending Review",
    bg: "bg-amber-100",
    text: "text-amber-700",
    icon: Clock,
  },
  kb_generating: {
    label: "Generating KB",
    bg: "bg-violet-100",
    text: "text-violet-600",
    icon: Loader2,
  },
  kb_generated: {
    label: "KB Generated",
    bg: "bg-violet-100",
    text: "text-violet-700",
    icon: Sparkles,
  },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.uploaded;
  const Icon = c.icon;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}
    >
      <Icon
        className={`w-3 h-3 mr-1 ${status === "parsing" || status === "embedding" ? "animate-spin" : ""}`}
      />
      {c.label}
    </span>
  );
};

const ALLOWED_EXT = [".pdf", ".docx", ".doc", ".txt", ".md", ".csv", ".zip"];
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB

const RAGDocumentManager = () => {
  const { token, isManager } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [facilityId, setFacilityId] = useState("");
  const [facilities, setFacilities] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [reviewNotes, setReviewNotes] = useState({});
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const [editingMeta, setEditingMeta] = useState(null);
  const [metadataDraft, setMetadataDraft] = useState({});
  const [sourceDraft, setSourceDraft] = useState({});
  const [metadataSchema, setMetadataSchema] = useState([]);
  const [savingMeta, setSavingMeta] = useState(false);
  const [verifyingUrl, setVerifyingUrl] = useState(false);
  const [expandedChildren, setExpandedChildren] = useState(new Set());

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setFacilities(list);
          if (list.length > 0 && !facilityId)
            setFacilityId(list[0].facility_id || "");
        }
      } catch (err) {
        console.error("Failed to load facilities:", err);
      }
    };
    fetchFacilities();
  }, [token]);

  useEffect(() => {
    if (!facilityId) return;
    const fac = facilities.find((f) => f.facility_id === facilityId);
    if (!fac) return;
    const vertical = fac.vertical_id || "retail";
    const facType = fac.facility_type || null;
    const fetchSchema = async () => {
      try {
        let url = `${AUTH_URL}/rag/metadata-schema?vertical_id=${encodeURIComponent(vertical)}`;
        if (facType) url += `&facility_type=${encodeURIComponent(facType)}`;
        const res = await fetch(url, { headers });
        if (res.ok) setMetadataSchema(await res.json());
      } catch (err) {
        console.error("Failed to load metadata schema:", err);
      }
    };
    fetchSchema();
  }, [facilityId, facilities, token]);

  const fetchDocuments = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      let url = `${AUTH_URL}/rag/documents?facility_id=${encodeURIComponent(facilityId)}`;
      if (statusFilter) url += `&status=${encodeURIComponent(statusFilter)}`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      setDocuments(Array.isArray(data) ? data : []);
      setSelectedDocs(new Set());
    } catch (err) {
      console.error("Failed to load documents:", err);
    }
    setLoading(false);
  }, [token, facilityId, statusFilter]);

  const fetchStats = useCallback(async () => {
    if (!facilityId) return;
    try {
      const res = await fetch(
        `${AUTH_URL}/rag/documents/stats?facility_id=${encodeURIComponent(facilityId)}`,
        { headers },
      );
      setStats(await res.json());
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }, [token, facilityId]);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [fetchDocuments, fetchStats]);

  const handleReview = async (id, action) => {
    try {
      const res = await fetch(`${AUTH_URL}/rag/documents/review`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id, action, notes: reviewNotes[id] || "" }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Review failed: ${err.error || res.status}`);
        return;
      }
      setReviewNotes((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      fetchDocuments();
      fetchStats();
    } catch (err) {
      console.error("Review failed:", err);
    }
  };

  const handleBulkReview = async (action) => {
    if (selectedDocs.size === 0) return;
    if (
      !window.confirm(
        `${action === "approve" ? "Approve" : "Deny"} ${selectedDocs.size} document(s)?`,
      )
    )
      return;
    try {
      const res = await fetch(`${AUTH_URL}/rag/documents/bulk-review`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ids: [...selectedDocs], action }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Bulk review failed: ${err.error || res.status}`);
        return;
      }
      setSelectedDocs(new Set());
      fetchDocuments();
      fetchStats();
    } catch (err) {
      console.error("Bulk review failed:", err);
    }
  };

  const uploadFiles = async (files) => {
    if (!facilityId) {
      alert("Select a facility first.");
      return;
    }
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        alert(`Skipping ${file.name} — unsupported type.`);
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        alert(
          `Skipping ${file.name} — file is ${(file.size / 1024 / 1024).toFixed(1)}MB (max ${MAX_UPLOAD_BYTES / 1024 / 1024}MB).`,
        );
        continue;
      }
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("facility_id", facilityId);
        const res = await fetch(`${AUTH_URL}/rag/documents/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const result = await res.json();
        if (result.success) {
          uploaded += result.uploaded_count || 1;
          if (result.skipped && result.skipped.length > 0) {
            alert(
              `${file.name}: ${result.uploaded_count} uploaded, ${result.skipped.length} skipped (${result.skipped.slice(0, 3).join(", ")}${result.skipped.length > 3 ? "..." : ""}).`,
            );
          }
        } else alert(`Upload failed for ${file.name}: ${result.error}`);
      } catch (err) {
        alert(`Upload error for ${file.name}: ${err.message}`);
      }
    }
    setUploading(false);
    if (uploaded > 0) {
      fetchDocuments();
      fetchStats();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  };
  const handleFileSelect = (e) => {
    uploadFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const SOURCE_META_KEYS = [
    "source_name",
    "source_url",
    "kb_source_type",
    "verified_at",
    "verified_by",
    "expires_at",
  ];

  const startEditingMeta = (doc) => {
    setEditingMeta(doc.id);
    const meta = doc.metadata || {};
    // Seed source draft from dedicated columns (preferred) then fall back to metadata JSONB
    setSourceDraft({
      source_name: doc.source_name || meta.source_name || "",
      source_url: doc.source_url || meta.source_url || "",
      kb_source_type: doc.kb_source_type || meta.kb_source_type || "",
      expires_at: doc.expires_at || meta.expires_at || "",
    });
    // Keep generic metadata draft free of source keys
    const genericMeta = Object.fromEntries(
      Object.entries(meta).filter(([k]) => !SOURCE_META_KEYS.includes(k)),
    );
    setMetadataDraft(genericMeta);
  };
  const cancelEditingMeta = () => {
    setEditingMeta(null);
    setMetadataDraft({});
    setSourceDraft({});
  };

  const saveMetadata = async (docId) => {
    if (!sourceDraft.source_name?.trim()) {
      alert("Source name is required before saving.");
      return;
    }
    if (
      !sourceDraft.source_url?.trim() ||
      !isValidUrl(sourceDraft.source_url)
    ) {
      alert("A valid source URL (http/https) is required before saving.");
      return;
    }
    setSavingMeta(true);
    try {
      // Merge source fields into metadata JSONB alongside generic metadata
      const merged = {
        ...metadataDraft,
        source_name: sourceDraft.source_name,
        source_url: sourceDraft.source_url,
        ...(sourceDraft.kb_source_type
          ? { kb_source_type: sourceDraft.kb_source_type }
          : {}),
        ...(sourceDraft.expires_at
          ? { expires_at: sourceDraft.expires_at }
          : {}),
      };
      const res = await fetch(`${AUTH_URL}/rag/documents/update-metadata`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id: docId, metadata: merged }),
      });
      const result = await res.json();
      if (result.success) {
        setEditingMeta(null);
        fetchDocuments();
      } else alert(`Save failed: ${result.error}`);
    } catch (err) {
      alert(`Save error: ${err.message}`);
    }
    setSavingMeta(false);
  };

  const markVerified = async (doc) => {
    const meta = doc.metadata || {};
    const updated = {
      ...meta,
      verified_at: new Date().toISOString(),
      verified_by: doc.reviewed_by || "admin",
    };
    try {
      const res = await fetch(`${AUTH_URL}/rag/documents/update-metadata`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id: doc.id, metadata: updated }),
      });
      if ((await res.json()).success) fetchDocuments();
    } catch {}
  };

  const handleDeleteVector = async (doc) => {
    if (
      !window.confirm(
        `Delete vector "${doc.vector_id}" from Pinecone? The document will return to "parsed" status.`,
      )
    )
      return;
    try {
      const res = await fetch(`${AUTH_URL}/rag/documents/delete-vector`, {
        method: "POST",
        headers,
        body: JSON.stringify({ id: doc.id }),
      });
      const result = await res.json();
      if (result.success) {
        fetchDocuments();
        fetchStats();
      } else alert(`Delete failed: ${result.error}`);
    } catch (err) {
      alert(`Delete error: ${err.message}`);
    }
  };

  const toggleSelect = (id) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedDocs.size === documents.length) setSelectedDocs(new Set());
    else setSelectedDocs(new Set(documents.map((d) => d.id)));
  };

  const totalStats = Object.values(stats).reduce(
    (acc, v) => ({ count: acc.count + (v.count || 0) }),
    { count: 0 },
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <FileText className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-slate-800">
            RAG Document Manager
          </h1>
        </div>
        <button
          onClick={() => {
            fetchDocuments();
            fetchStats();
          }}
          disabled={loading}
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center"
        >
          <RefreshCw
            className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`}
          />{" "}
          Refresh
        </button>
      </div>

      <div className="flex items-end space-x-4">
        <div className="flex-1">
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Facility
          </label>
          <select
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select facility...</option>
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.facility_name || f.facility_id}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Status Filter
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>
                {cfg.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {facilityId && (
        <div className="flex items-center space-x-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded-lg text-xs text-violet-700">
          <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            Uploaded documents are chunked into KB rows automatically after
            admin approval. Approved KB rows are then embedded into Pinecone.
          </span>
        </div>
      )}

      {facilityId &&
        (() => {
          const fac = facilities.find((f) => f.facility_id === facilityId);
          const selfServiceEnabled = fac?.self_service_upload === true;
          const canUpload = isManager || selfServiceEnabled;
          if (!canUpload) {
            return (
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 text-center">
                <p className="text-sm text-slate-500">
                  Self-service uploads are disabled for this facility. Contact
                  your administrator to enable uploads.
                </p>
              </div>
            );
          }
          return (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-300 bg-white hover:border-blue-400 hover:bg-slate-50"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ALLOWED_EXT.join(",")}
                onChange={handleFileSelect}
                className="hidden"
              />
              {uploading ? (
                <div className="flex items-center justify-center space-x-2 text-blue-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm font-medium">Uploading...</span>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">
                    Drag and drop files here, or click to browse
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Supported: {ALLOWED_EXT.join(", ")} · max{" "}
                    {MAX_UPLOAD_BYTES / 1024 / 1024}MB per file
                  </p>
                </>
              )}
            </div>
          );
        })()}

      {facilityId && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const s = stats[key];
            if (!s) return null;
            const Icon = cfg.icon;
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(statusFilter === key ? "" : key)}
                className={`px-4 py-2 rounded-lg border text-sm flex items-center space-x-2 transition-colors ${
                  statusFilter === key
                    ? "ring-2 ring-blue-500 border-blue-300"
                    : "border-slate-200 hover:bg-slate-50"
                } ${cfg.bg}`}
              >
                <Icon className={`w-4 h-4 ${cfg.text}`} />
                <span className={`font-bold ${cfg.text}`}>{s.count}</span>
                <span className="text-slate-500">{cfg.label}</span>
              </button>
            );
          })}
          {totalStats.count > 0 && (
            <div className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm flex items-center space-x-2">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="font-bold text-slate-700">
                {totalStats.count}
              </span>
              <span className="text-slate-500">Total</span>
            </div>
          )}
        </div>
      )}

      {selectedDocs.size > 0 && (
        <div className="flex items-center justify-between bg-blue-50 rounded-lg px-4 py-2 border border-blue-200">
          <span className="text-sm text-blue-700">
            {selectedDocs.size} document(s) selected
          </span>
          {isManager && (
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkReview("approve")}
                className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg flex items-center"
              >
                <Check className="w-3.5 h-3.5 mr-1" /> Approve Selected
              </button>
              <button
                onClick={() => handleBulkReview("deny")}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Deny Selected
              </button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 w-8">
                <input
                  type="checkbox"
                  checked={
                    selectedDocs.size === documents.length &&
                    documents.length > 0
                  }
                  onChange={toggleSelectAll}
                  className="h-4 w-4 text-blue-600 rounded border-slate-300"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Document
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Source
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Metadata
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading && documents.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-8 text-center text-slate-500"
                >
                  Loading documents...
                </td>
              </tr>
            ) : documents.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-8 text-center text-slate-500"
                >
                  {facilityId
                    ? "No documents found. Upload files above or via SFTP."
                    : "Select a facility to view documents."}
                </td>
              </tr>
            ) : (
              documents.map((doc) => {
                const isExpanded = expandedDoc === doc.id;
                const meta = doc.metadata || {};
                // Source docs: uploaded/sftp with no parent. KB rows + Q&A: have parent_doc_id or source_type=kb_generated/qa_pair
                const isSourceDoc =
                  !doc.parent_doc_id &&
                  (doc.source_type === "upload" ||
                    doc.source_type === "sftp" ||
                    (!doc.source_type && doc.chunk_total === 1));
                const children = isSourceDoc
                  ? documents.filter((d) => d.parent_doc_id === doc.id)
                  : [];
                const childrenShown =
                  isSourceDoc && expandedChildren.has(doc.id);
                const canReview =
                  isManager &&
                  (doc.status === "parsed" ||
                    doc.status === "approved" ||
                    doc.status === "denied" ||
                    doc.status === "pending_review" ||
                    doc.status === "uploaded");
                const isEditing = editingMeta === doc.id;
                const approveTitle = isSourceDoc
                  ? "Approve & generate KB rows"
                  : "Approve";

                return (
                  <React.Fragment key={doc.id}>
                    <tr
                      className={`hover:bg-slate-50 ${doc.parent_doc_id ? "bg-violet-50/30" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedDocs.has(doc.id)}
                          onChange={() => toggleSelect(doc.id)}
                          className="h-4 w-4 text-blue-600 rounded border-slate-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start space-x-1.5">
                          {doc.parent_doc_id && (
                            <span className="mt-0.5 text-violet-400 flex-shrink-0">
                              ↳
                            </span>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800 max-w-xs truncate">
                              {doc.title ||
                                doc.source_filename ||
                                `Document #${doc.id}`}
                            </div>
                            {doc.chunk_total > 1 && (
                              <span className="text-xs text-slate-400">
                                Chunk {doc.chunk_index + 1}/{doc.chunk_total}
                              </span>
                            )}
                            <div className="text-xs text-slate-400 mt-0.5">
                              {doc.content
                                ? `${doc.content.length.toLocaleString()} chars`
                                : ""}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-600">
                          {doc.source_type || "sftp"}
                        </div>
                        <div className="text-xs text-slate-400 font-mono truncate max-w-[200px]">
                          {doc.source_filename}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const srcName = doc.source_name || meta.source_name;
                          const srcUrl = doc.source_url || meta.source_url;
                          const verifiedAt =
                            doc.verified_at || meta.verified_at;
                          const expiresAt = doc.expires_at || meta.expires_at;
                          const stale = verifiedAt && daysAgo(verifiedAt) > 90;
                          const expired =
                            expiresAt && new Date(expiresAt) < new Date();
                          return (
                            <div className="flex flex-wrap gap-1">
                              {meta.doc_type && (
                                <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                  {meta.doc_type}
                                </span>
                              )}
                              {meta.category && (
                                <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                                  {meta.category}
                                </span>
                              )}
                              {meta.topic && (
                                <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">
                                  {meta.topic}
                                </span>
                              )}
                              {srcName && (
                                <span
                                  className="px-1.5 py-0.5 text-xs bg-emerald-50 text-emerald-700 rounded flex items-center gap-0.5 max-w-[120px] truncate"
                                  title={srcName}
                                >
                                  <Link2 className="w-2.5 h-2.5 flex-shrink-0" />
                                  {srcName}
                                </span>
                              )}
                              {!srcName && (
                                <span className="px-1.5 py-0.5 text-xs bg-amber-50 text-amber-700 rounded flex items-center gap-0.5">
                                  <AlertTriangle className="w-2.5 h-2.5" />
                                  No source
                                </span>
                              )}
                              {stale && (
                                <span className="px-1.5 py-0.5 text-xs bg-red-50 text-red-600 rounded font-medium">
                                  Stale {daysAgo(verifiedAt)}d
                                </span>
                              )}
                              {expired && (
                                <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">
                                  Expired
                                </span>
                              )}
                              {verifiedAt && !stale && (
                                <span
                                  className="px-1.5 py-0.5 text-xs bg-green-50 text-green-700 rounded flex items-center gap-0.5"
                                  title={`Verified ${daysAgo(verifiedAt)} days ago`}
                                >
                                  <ShieldCheck className="w-2.5 h-2.5" />
                                  {daysAgo(verifiedAt)}d ago
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1.5">
                          <StatusBadge status={doc.status} />
                          {isSourceDoc && children.length > 0 && (
                            <button
                              onClick={() =>
                                setExpandedChildren((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(doc.id)) next.delete(doc.id);
                                  else next.add(doc.id);
                                  return next;
                                })
                              }
                              className="px-1.5 py-0.5 text-xs bg-violet-100 text-violet-700 rounded hover:bg-violet-200"
                              title="Show KB rows"
                            >
                              {children.length} KB row
                              {children.length !== 1 ? "s" : ""}
                              {childrenShown ? " ▲" : " ▼"}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() =>
                              setExpandedDoc(isExpanded ? null : doc.id)
                            }
                            className="p-1 text-slate-400 hover:text-blue-600 rounded"
                            title="View details"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                          {canReview && doc.status !== "approved" && (
                            <button
                              onClick={() => handleReview(doc.id, "approve")}
                              className="p-1 text-green-500 hover:text-green-700 hover:bg-green-50 rounded"
                              title={approveTitle}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {canReview && doc.status !== "denied" && (
                            <button
                              onClick={() => handleReview(doc.id, "deny")}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                              title="Deny"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          {doc.status === "embedded" && doc.vector_id && (
                            <button
                              onClick={() => handleDeleteVector(doc)}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete from Pinecone"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-4 bg-slate-50 border-t border-slate-100"
                        >
                          <div className="grid grid-cols-2 gap-6">
                            <div>
                              <h4 className="text-xs font-medium text-slate-500 mb-2 uppercase">
                                Content Preview
                              </h4>
                              <p className="text-xs text-slate-600 bg-white p-3 rounded border max-h-48 overflow-y-auto whitespace-pre-wrap">
                                {doc.content
                                  ? doc.content.substring(0, 2000)
                                  : "No content"}
                                {doc.content &&
                                  doc.content.length > 2000 &&
                                  "..."}
                              </p>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-xs font-medium text-slate-500 uppercase">
                                    Metadata
                                  </h4>
                                  {!isEditing &&
                                    (doc.status === "parsed" ||
                                      doc.status === "uploaded") && (
                                      <button
                                        onClick={() => startEditingMeta(doc)}
                                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                                      >
                                        <Edit3 className="w-3 h-3 mr-1" /> Edit
                                      </button>
                                    )}
                                  {isEditing && (
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => saveMetadata(doc.id)}
                                        disabled={savingMeta}
                                        className="text-xs text-green-600 hover:text-green-800 flex items-center"
                                      >
                                        <Save className="w-3 h-3 mr-1" />{" "}
                                        {savingMeta ? "Saving..." : "Save"}
                                      </button>
                                      <button
                                        onClick={cancelEditingMeta}
                                        className="text-xs text-slate-500 hover:text-slate-700"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {isEditing ? (
                                  <div className="space-y-4 bg-white p-3 rounded border">
                                    {/* ── Source Attribution (required) ── */}
                                    <div className="space-y-2">
                                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
                                        <Link2 className="w-3 h-3" /> Source
                                        Attribution
                                        <span className="text-red-500">*</span>
                                      </p>
                                      <div className="flex items-center text-xs gap-2">
                                        <label className="font-medium text-slate-600 w-32 flex-shrink-0">
                                          Source Type
                                        </label>
                                        <select
                                          value={
                                            sourceDraft.kb_source_type || ""
                                          }
                                          onChange={(e) =>
                                            setSourceDraft((p) => ({
                                              ...p,
                                              kb_source_type: e.target.value,
                                            }))
                                          }
                                          className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                                        >
                                          <option value="">Select type…</option>
                                          {KB_SOURCE_TYPES.map((t) => (
                                            <option
                                              key={t.value}
                                              value={t.value}
                                            >
                                              {t.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="flex items-center text-xs gap-2">
                                        <label className="font-medium text-slate-600 w-32 flex-shrink-0">
                                          Source Name{" "}
                                          <span className="text-red-500">
                                            *
                                          </span>
                                        </label>
                                        <input
                                          type="text"
                                          value={sourceDraft.source_name || ""}
                                          onChange={(e) =>
                                            setSourceDraft((p) => ({
                                              ...p,
                                              source_name: e.target.value,
                                            }))
                                          }
                                          placeholder="e.g. AZ Secretary of State"
                                          className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                                        />
                                      </div>
                                      <div className="flex items-center text-xs gap-2">
                                        <label className="font-medium text-slate-600 w-32 flex-shrink-0">
                                          Source URL{" "}
                                          <span className="text-red-500">
                                            *
                                          </span>
                                        </label>
                                        <input
                                          type="url"
                                          value={sourceDraft.source_url || ""}
                                          onChange={(e) =>
                                            setSourceDraft((p) => ({
                                              ...p,
                                              source_url: e.target.value,
                                            }))
                                          }
                                          placeholder="https://…"
                                          className={`flex-1 px-2 py-1 border rounded text-xs focus:ring-1 focus:ring-blue-500 ${
                                            sourceDraft.source_url &&
                                            !isValidUrl(sourceDraft.source_url)
                                              ? "border-red-400 bg-red-50"
                                              : "border-slate-300"
                                          }`}
                                        />
                                        {isValidUrl(sourceDraft.source_url) && (
                                          <a
                                            href={sourceDraft.source_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                                            title="Open URL to verify"
                                          >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                          </a>
                                        )}
                                      </div>
                                      <div className="flex items-center text-xs gap-2">
                                        <label className="font-medium text-slate-600 w-32 flex-shrink-0">
                                          Expires At
                                        </label>
                                        <input
                                          type="datetime-local"
                                          value={
                                            sourceDraft.expires_at
                                              ? sourceDraft.expires_at.slice(
                                                  0,
                                                  16,
                                                )
                                              : ""
                                          }
                                          onChange={(e) =>
                                            setSourceDraft((p) => ({
                                              ...p,
                                              expires_at: e.target.value
                                                ? new Date(
                                                    e.target.value,
                                                  ).toISOString()
                                                : "",
                                            }))
                                          }
                                          className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                                        />
                                      </div>
                                    </div>
                                    {/* ── Generic metadata fields ── */}
                                    {(metadataSchema.length > 0
                                      ? metadataSchema
                                      : Object.keys(metadataDraft).map((k) => ({
                                          field_name: k,
                                        }))
                                    ).map((field) => (
                                      <div
                                        key={field.field_name}
                                        className="flex items-center text-xs"
                                      >
                                        <label className="font-medium text-slate-600 w-32 flex-shrink-0">
                                          {field.field_name}
                                          {field.required && (
                                            <span className="text-red-500 ml-0.5">
                                              *
                                            </span>
                                          )}
                                        </label>
                                        <input
                                          type="text"
                                          value={
                                            metadataDraft[field.field_name] ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            setMetadataDraft((prev) => ({
                                              ...prev,
                                              [field.field_name]:
                                                e.target.value,
                                            }))
                                          }
                                          placeholder={field.description || ""}
                                          className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    {Object.entries(meta)
                                      .filter(
                                        ([k]) => !SOURCE_META_KEYS.includes(k),
                                      )
                                      .sort(([a], [b]) => a.localeCompare(b))
                                      .map(([k, v]) => (
                                        <div key={k} className="flex text-xs">
                                          <span className="font-medium text-slate-600 w-32 flex-shrink-0">
                                            {k}:
                                          </span>
                                          <span className="text-slate-500">
                                            {typeof v === "object"
                                              ? JSON.stringify(v)
                                              : String(v)}
                                          </span>
                                        </div>
                                      ))}
                                    {Object.keys(meta).filter(
                                      (k) => !SOURCE_META_KEYS.includes(k),
                                    ).length === 0 && (
                                      <span className="text-xs text-slate-400">
                                        No metadata populated yet.
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* ── Source Attribution (read-only view) ── */}
                              {(() => {
                                const srcName =
                                  doc.source_name || meta.source_name;
                                const srcUrl =
                                  doc.source_url || meta.source_url;
                                const srcType =
                                  doc.kb_source_type || meta.kb_source_type;
                                const verifiedAt =
                                  doc.verified_at || meta.verified_at;
                                const verifiedBy =
                                  doc.verified_by || meta.verified_by;
                                const expiresAt =
                                  doc.expires_at || meta.expires_at;
                                const stale =
                                  verifiedAt && daysAgo(verifiedAt) > 90;
                                const expired =
                                  expiresAt && new Date(expiresAt) < new Date();
                                return (
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <h4 className="text-xs font-medium text-slate-500 uppercase flex items-center gap-1">
                                        <Link2 className="w-3 h-3" /> Source
                                        Attribution
                                      </h4>
                                      {!isEditing && verifiedAt && (
                                        <button
                                          onClick={() => markVerified(doc)}
                                          className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                                          title="Mark as re-verified now"
                                        >
                                          <ShieldCheck className="w-3 h-3" />{" "}
                                          Re-verify
                                        </button>
                                      )}
                                    </div>
                                    <div className="space-y-1 text-xs">
                                      {srcName ? (
                                        <>
                                          <div className="flex items-start">
                                            <span className="font-medium text-slate-600 w-32 flex-shrink-0">
                                              Source:
                                            </span>
                                            <span className="text-slate-700 font-medium">
                                              {srcName}
                                              {srcType && (
                                                <span className="ml-1.5 text-slate-400 font-normal">
                                                  (
                                                  {KB_SOURCE_TYPES.find(
                                                    (t) => t.value === srcType,
                                                  )?.label || srcType}
                                                  )
                                                </span>
                                              )}
                                            </span>
                                          </div>
                                          {srcUrl && (
                                            <div className="flex items-center">
                                              <span className="font-medium text-slate-600 w-32 flex-shrink-0">
                                                URL:
                                              </span>
                                              <a
                                                href={srcUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline truncate max-w-xs flex items-center gap-1"
                                              >
                                                {srcUrl}
                                                <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                                              </a>
                                            </div>
                                          )}
                                          <div className="flex items-center">
                                            <span className="font-medium text-slate-600 w-32 flex-shrink-0">
                                              Verified:
                                            </span>
                                            {verifiedAt ? (
                                              <span
                                                className={`font-medium ${stale ? "text-red-600" : "text-emerald-600"}`}
                                              >
                                                {stale ? "⚠ " : "✓ "}
                                                {daysAgo(verifiedAt)} days ago
                                                {verifiedBy &&
                                                  ` by ${verifiedBy}`}
                                                {stale &&
                                                  " — needs re-verification"}
                                              </span>
                                            ) : (
                                              <span className="text-amber-600">
                                                Not yet verified
                                              </span>
                                            )}
                                          </div>
                                          {!verifiedAt && !isEditing && (
                                            <button
                                              onClick={() => markVerified(doc)}
                                              className="mt-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 flex items-center gap-1"
                                            >
                                              <ShieldCheck className="w-3 h-3" />{" "}
                                              Mark as Verified
                                            </button>
                                          )}
                                          {expiresAt && (
                                            <div className="flex items-center">
                                              <span className="font-medium text-slate-600 w-32 flex-shrink-0">
                                                Expires:
                                              </span>
                                              <span
                                                className={
                                                  expired
                                                    ? "text-slate-500 line-through flex items-center gap-1"
                                                    : "text-slate-600"
                                                }
                                              >
                                                {expired && (
                                                  <CalendarX className="w-3 h-3 text-slate-400" />
                                                )}
                                                {new Date(
                                                  expiresAt,
                                                ).toLocaleDateString()}
                                                {expired && " (expired)"}
                                              </span>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                          No source attributed — edit to add
                                          source before approving.
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}

                              <div>
                                <h4 className="text-xs font-medium text-slate-500 mb-2 uppercase">
                                  Details
                                </h4>
                                <div className="space-y-1 text-xs">
                                  {doc.vector_id && (
                                    <div className="flex">
                                      <span className="font-medium text-slate-600 w-32">
                                        Vector ID:
                                      </span>
                                      <span className="text-slate-500 font-mono">
                                        {doc.vector_id}
                                      </span>
                                    </div>
                                  )}
                                  {doc.pinecone_namespace && (
                                    <div className="flex">
                                      <span className="font-medium text-slate-600 w-32">
                                        Namespace:
                                      </span>
                                      <span className="text-slate-500 font-mono">
                                        {doc.pinecone_namespace}
                                      </span>
                                    </div>
                                  )}
                                  {doc.reviewed_by && (
                                    <div className="flex">
                                      <span className="font-medium text-slate-600 w-32">
                                        Reviewed by:
                                      </span>
                                      <span className="text-slate-500">
                                        {doc.reviewed_by}
                                      </span>
                                    </div>
                                  )}
                                  {doc.review_notes && (
                                    <div className="flex">
                                      <span className="font-medium text-slate-600 w-32">
                                        Notes:
                                      </span>
                                      <span className="text-slate-500">
                                        {doc.review_notes}
                                      </span>
                                    </div>
                                  )}
                                  {doc.parse_error && (
                                    <div className="mt-2 bg-red-50 border border-red-200 rounded p-2">
                                      <span className="font-medium text-red-600">
                                        Parse Error:{" "}
                                      </span>
                                      <span className="text-red-500">
                                        {doc.parse_error}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {(doc.status === "parsed" ||
                                doc.status === "pending_review" ||
                                doc.status === "uploaded") &&
                                isManager && (
                                  <div>
                                    <h4 className="text-xs font-medium text-slate-500 mb-2 uppercase">
                                      Review
                                    </h4>
                                    {isSourceDoc && (
                                      <p className="text-xs text-violet-600 mb-2">
                                        Approving this document will chunk it
                                        into KB rows for further review before
                                        embedding.
                                      </p>
                                    )}
                                    <input
                                      type="text"
                                      value={reviewNotes[doc.id] || ""}
                                      onChange={(e) =>
                                        setReviewNotes((prev) => ({
                                          ...prev,
                                          [doc.id]: e.target.value,
                                        }))
                                      }
                                      placeholder="Optional review notes..."
                                      className="w-full px-3 py-1.5 border border-slate-300 rounded text-xs mb-2 focus:ring-2 focus:ring-blue-500"
                                    />
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() =>
                                          handleReview(doc.id, "approve")
                                        }
                                        className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded flex items-center"
                                      >
                                        <Check className="w-3 h-3 mr-1" />
                                        {isSourceDoc
                                          ? "Approve & Generate KB"
                                          : "Approve"}
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleReview(doc.id, "deny")
                                        }
                                        className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded flex items-center"
                                      >
                                        <X className="w-3 h-3 mr-1" /> Deny
                                      </button>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* KB row count indicator — review happens in KB Review Queue */}
                    {childrenShown && children.length > 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-2 bg-violet-50/40 border-t border-violet-100"
                        >
                          <p className="text-xs text-violet-600 flex items-center">
                            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                            {children.length} KB row
                            {children.length !== 1 ? "s" : ""} generated —
                            review and approve them in{" "}
                            <span className="font-medium ml-1">
                              Knowledge Base → KB Review Queue
                            </span>
                          </p>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RAGDocumentManager;
